import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";

import { FetchAccountsPayload } from "@admin/api/account";
import { useFetchAccounts } from "@admin/hooks/useFetchAccounts";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { Box, TableRow } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { useFullPageTableLayoutStyles } from "@shared/table/FullPageTableLayout.style";
import { MinimalTable, MinimalTableCell, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import { useContainerOverflow } from "@shared/table/useContainerOverflow";
import {
    createColumnHelper,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<API.Account>();
const tableColumns = [
    columnHelper.accessor("id", {
        id: "id",
        header: "Id",
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
        enableSorting: false,
    }),
    columnHelper.accessor("name", {
        id: "name",
        header: i18next.t("common:label.name"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("email", {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
];

const defaultParams: Required<FetchAccountsPayload> = {
    PageIndex: 0,
    PageSize: 12,
    SortCriteria: "email",
    SortDirection: 0,
    HasRoles: false,
    ExcludeDeleted: false,
};

const AdminAccountsPage = () => {
    const [params, setParams] = React.useState<Required<FetchAccountsPayload>>(defaultParams);
    const { data: accounts, isLoading, count: accountsCount } = useFetchAccounts(params);

    const { classes: layoutClasses, cx } = useFullPageTableLayoutStyles();
    const { t } = useTranslation(["pages"]);

    const { ref: tableContainerRef, contentOverflows } = useContainerOverflow(true, [
        params.PageIndex,
        params.PageSize,
        accounts.length,
    ]);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, [])[0];
        setParams((prev) => ({ ...prev, SortCriteria: newValue.id, SortDirection: prev.SortDirection ? 0 : 1, PageIndex: 0 }));
    };

    const table = useReactTable<API.Account>({
        data: accounts,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: onSortingChange,
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <PageContainer fitViewport>
            <Box className={layoutClasses.tableLayout}>
                <Box className={layoutClasses.tableWrapper}>
                    <MinimalTableContainer
                        ref={tableContainerRef}
                        className={cx(layoutClasses.tableContainer, !contentOverflows && layoutClasses.tableContainerNoScroll)}
                    >
                        <MinimalTable>
                            <MinimalTableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <Header key={header.id} {...header} />
                                        ))}
                                    </TableRow>
                                ))}
                            </MinimalTableHeader>

                            <TableBody
                                table={table}
                                loading={isLoading}
                                noDataMessage={t("pages:adminAccountsPage.noUsersToDisplay")}
                            >
                                {table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) =>
                                            flexRender(cell.column.columnDef.cell, {
                                                ...cell.getContext(),
                                                key: cell.id,
                                            }),
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </MinimalTable>
                    </MinimalTableContainer>
                </Box>
                <CustomTablePagination
                    component="div"
                    className={layoutClasses.pagination}
                    showFirstButton
                    showLastButton
                    rowsPerPageOptions={[12, 24, 48]}
                    count={accountsCount}
                    rowsPerPage={params.PageSize}
                    page={params.PageIndex}
                    onPageChange={(_event, page) => {
                        setParams((prev) => ({ ...prev, PageIndex: page }));
                    }}
                    onRowsPerPageChange={(e) => {
                        const nextSize = Number(e.target.value);
                        setParams((prev) => ({ ...prev, PageSize: nextSize, PageIndex: 0 }));
                    }}
                />
            </Box>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminAccountsPage, {
        title: i18next.t("pages:adminAccountsPage.metaTitle"),
        description: i18next.t("pages:adminAccountsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
